import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCostsAndFeesContext } from '../../context/CostsAndFeesProvider';
import type { BedSelector, Selector, UnitSelector as UnitSelectorType } from 'app/types/costAndFees.type';
import { EstimateTitle, FeesSidebarWrapper, FeeType, getRoomButtonStyles, Price, RoomsRow, Text } from './styles';
import UnitSelector from '../UnitSelector';

const getBedDisplayName = (count: number): string => {
  if (count === 0) {
    return 'Studio';
  }
  return `${count} bd`;
};

export default function CostsFilters() {
  const { calculatedCostsAndFees, setSelectorOverrides, getDisplayAmount } = useCostsAndFeesContext();

  const [selectedBedCounts, setSelectedBedCounts] = useState<Array<number>>([]);
  const [allBedsSelected, setAllBedsSelected] = useState(true);

  const [selectedUnit, setSelectedUnit] = useState<UnitSelectorType | null>(null);

  const bedOptions = useMemo(() => {
    const options: Array<{ displayName: string; numBeds?: number }> = [{ displayName: 'All' }];
    if (calculatedCostsAndFees?.selectors?.beds) {
      calculatedCostsAndFees.selectors.beds.forEach((bed: BedSelector) => {
        if (bed.numBeds !== undefined) {
          options.push({
            displayName: bed.selector?.displayName ?? getBedDisplayName(bed.numBeds),
            numBeds: bed.numBeds,
          });
        }
      });
    }
    return [options[0], ...options.slice(1).sort((a, b) => (a.numBeds ?? -1) - (b.numBeds ?? -1))];
  }, [calculatedCostsAndFees?.selectors?.beds]);

  const allNumericBedCounts = useMemo(() => {
    return bedOptions
      .filter(
        (opt): opt is { displayName: string; numBeds: number } =>
          opt.displayName !== 'All' && opt.numBeds !== undefined,
      )
      .map((opt) => opt.numBeds)
      .sort((a, b) => a - b);
  }, [bedOptions]);

  const displayedMonthlyFee = useMemo(() => {
    // Always use calculated costs which include selected fees
    return calculatedCostsAndFees?.costs?.monthly?.amount;
  }, [calculatedCostsAndFees]);

  const estimateTitle = useMemo(() => {
    if (selectedUnit) {
      const unitDisplayName = selectedUnit.selector.displayName || getBedDisplayName(selectedUnit.numBeds);
      return `Est. total for ${unitDisplayName}`;
    }
    const showAll =
      allBedsSelected || selectedBedCounts.length === 0 || selectedBedCounts.length === allNumericBedCounts.length;
    if (showAll && allNumericBedCounts.length > 0) {
      if (allNumericBedCounts.length === 1) {
        return `Est. total for ${getBedDisplayName(allNumericBedCounts[0])}`;
      }
      const minBeds = Math.min(...allNumericBedCounts);
      const maxBeds = Math.max(...allNumericBedCounts);
      const minDisplay = getBedDisplayName(minBeds);
      const maxDisplay = getBedDisplayName(maxBeds);
      return `Est. total for ${minDisplay} - ${maxDisplay}`;
    }
    if (showAll && allNumericBedCounts.length === 0) {
      return 'Est. total for all units';
    }
    if (selectedBedCounts.length === 1) {
      return `Est. total for ${getBedDisplayName(selectedBedCounts[0])}`;
    }
    if (selectedBedCounts.length > 1) {
      const minBeds = Math.min(...selectedBedCounts);
      const maxBeds = Math.max(...selectedBedCounts);
      const minDisplay = getBedDisplayName(minBeds);
      const maxDisplay = getBedDisplayName(maxBeds);
      return `Est. total for ${minDisplay} - ${maxDisplay}`;
    }
    return 'Estimated total';
  }, [selectedUnit, allBedsSelected, selectedBedCounts, allNumericBedCounts]);

  const bedSelectors = useMemo(
    () => calculatedCostsAndFees?.selectors?.beds || [],
    [calculatedCostsAndFees?.selectors?.beds],
  );

  const selectAllBeds = useCallback(() => {
    setSelectedBedCounts([]);
    setAllBedsSelected(true);
    setSelectedUnit(null);
  }, []);

  const updateBedSelection = useCallback(
    (numBeds: number) => {
      setSelectedUnit(null);

      setSelectedBedCounts((prevSelected) => {
        const isCurrentlySelected = prevSelected.includes(numBeds);
        const wasAllSelected = allBedsSelected;

        let newSelected: Array<number>;

        if (isCurrentlySelected) {
          if (prevSelected.length > 1) {
            newSelected = prevSelected.filter((bed) => bed !== numBeds);
          } else {
            newSelected = [];
          }
        } else {
          if (wasAllSelected || prevSelected.length === 0) {
            newSelected = [numBeds];
          } else {
            newSelected = [...prevSelected, numBeds].sort((a, b) => a - b);
          }
        }

        if (
          newSelected.length === 0 ||
          (allNumericBedCounts.length > 0 && newSelected.length === allNumericBedCounts.length)
        ) {
          setAllBedsSelected(true);
          return [];
        } else {
          setAllBedsSelected(false);
          return newSelected;
        }
      });
    },
    [allBedsSelected, allNumericBedCounts],
  );

  const handleUnitSelect = useCallback(
    (unit: UnitSelectorType | null) => {
      setSelectedUnit(unit);
      if (unit) {
        setAllBedsSelected(false);
        setSelectedBedCounts([unit.numBeds]);
      } else {
        selectAllBeds();
      }
    },
    [selectAllBeds],
  );

  useEffect(() => {
    if (!setSelectorOverrides) return;

    let newOverrides: Array<Selector> = [];

    if (selectedUnit) {
      newOverrides = [selectedUnit.selector];
    } else if (!allBedsSelected && selectedBedCounts.length > 0) {
      newOverrides = bedSelectors
        .filter((bed: BedSelector) => bed.numBeds !== undefined && selectedBedCounts.includes(bed.numBeds))
        .map((bed: BedSelector) => bed.selector);
    }

    setSelectorOverrides(newOverrides);
  }, [selectedUnit, selectedBedCounts, allBedsSelected, bedSelectors, setSelectorOverrides]);

  const shouldRenderSidebar = () => {
    if (!calculatedCostsAndFees?.selectors) {
      return false;
    }
    const hasMultipleBedTypes = (calculatedCostsAndFees.selectors.beds?.length ?? 0) > 1;
    const hasMultipleUnits = (calculatedCostsAndFees.selectors.units?.length ?? 0) > 1;
    return hasMultipleBedTypes || hasMultipleUnits;
  };

  if (!shouldRenderSidebar()) {
    return null;
  }

  return (
    <FeesSidebarWrapper data-testid="costs-filters-sidebar">
      {(calculatedCostsAndFees?.selectors?.beds?.length ?? 0) > 1 && (
        <RoomsRow>
          <button className={getRoomButtonStyles(allBedsSelected && !selectedUnit)} key="All" onClick={selectAllBeds}>
            All
          </button>
          {bedOptions
            .filter((opt) => opt.displayName !== 'All')
            .map((option) => (
              <button
                className={getRoomButtonStyles(
                  !allBedsSelected &&
                    !selectedUnit &&
                    option.numBeds !== undefined &&
                    selectedBedCounts.includes(option.numBeds),
                )}
                key={option.displayName}
                onClick={() => {
                  if (option.numBeds !== undefined) {
                    updateBedSelection(option.numBeds);
                  }
                }}
              >
                {option.displayName}
              </button>
            ))}
        </RoomsRow>
      )}

      {(calculatedCostsAndFees?.selectors?.units?.length ?? 0) > 0 && (
        <>
          <Text>Choose a unit to estimate your total</Text>
          <UnitSelector
            selectedUnit={selectedUnit}
            onUnitSelect={handleUnitSelect}
            selectedBedCounts={selectedBedCounts}
            allBedsSelected={allBedsSelected}
          />
        </>
      )}

      <EstimateTitle>{estimateTitle}</EstimateTitle>
      <div>
        <Price>{getDisplayAmount?.(displayedMonthlyFee) ?? 'N/A'}</Price>
        <FeeType>Monthly rent & fees</FeeType>
      </div>
      <div>
        <Price>{getDisplayAmount?.(calculatedCostsAndFees?.costs?.moveIn?.amount) ?? 'N/A'}</Price>
        <FeeType>One-time fees</FeeType>
      </div>
    </FeesSidebarWrapper>
  );
}
